import { useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import GlobalStyles from '@mui/material/GlobalStyles'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PrintIcon from '@mui/icons-material/Print'
import ContentFrame, { PageHeading, SectionHeading } from '../components/assessment/ContentFrame'
import { errorMessage, getEnrolment, getQuestionBank, isOk } from '../helpers/trainingApi'

const PRINT_ID = 'training-question-bank'

// Prints the bank on its own: everything else in the app frame is hidden
// and the frame's scroll containers are released so every page prints.
const printStyles = {
  '@media print': {
    'html, body, #root': { height: 'auto !important', overflow: 'visible !important' },
    'body *': { visibility: 'hidden' },
    [`#${PRINT_ID}, #${PRINT_ID} *`]: { visibility: 'visible' },
    [`#${PRINT_ID}`]: { position: 'absolute', left: 0, top: 0, width: '100%', maxWidth: 'none', padding: 0 },
    [`#${PRINT_ID} .no-print`]: { display: 'none !important' },
    [`#${PRINT_ID} .bank-question`]: { breakInside: 'avoid' },
  },
}

const QuestionBank = () => {
  const { enrolmentId } = useParams()
  const coursePath = `/training/course/${enrolmentId}`

  // 'loading' | 'notReviewer' | 'error' | 'ready'
  const [step, setStep] = useState('loading')
  const [error, setError] = useState('')
  const [bank, setBank] = useState(null)
  const [enrolment, setEnrolment] = useState(null)
  const [outcomeFilter, setOutcomeFilter] = useState('')
  const [moduleFilter, setModuleFilter] = useState('')
  const headingRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    setStep('loading')
    // The enrolment is only used for the course and module titles.
    Promise.all([getQuestionBank(enrolmentId), getEnrolment(enrolmentId)]).then(([bankRes, enrolmentRes]) => {
      if (cancelled) return
      if (bankRes.status === 404) return setStep('notReviewer')
      if (!isOk(bankRes)) {
        setError(errorMessage(bankRes, 'We could not load the question bank. Please try again.'))
        return setStep('error')
      }
      setBank(bankRes.data)
      setEnrolment(isOk(enrolmentRes) ? enrolmentRes.data : null)
      return setStep('ready')
    })
    return () => {
      cancelled = true
    }
  }, [enrolmentId])

  useEffect(() => {
    if (step !== 'loading') headingRef.current?.focus()
  }, [step])

  const questions = bank?.questions ?? []
  const outcomes = bank?.outcomes ?? {}
  const moduleTitles = useMemo(
    () => Object.fromEntries((enrolment?.content?.modules ?? []).map((module) => [module.id, module.title])),
    [enrolment],
  )
  const moduleLabel = (moduleId) => (moduleTitles[moduleId] ? `${moduleTitles[moduleId]} (${moduleId})` : moduleId)
  const pathwayTitle = (pathwayId) => bank?.pathways?.find((pathway) => pathway.id === pathwayId)?.title ?? pathwayId

  const outcomeIds = useMemo(
    () => [...new Set(questions.flatMap((question) => question.outcomes ?? []))].sort(),
    [questions],
  )
  const moduleIds = useMemo(() => [...new Set(questions.map((question) => question.module))], [questions])

  const shown = questions.filter(
    (question) =>
      (!outcomeFilter || question.outcomes?.includes(outcomeFilter)) &&
      (!moduleFilter || question.module === moduleFilter),
  )

  if (step === 'loading') {
    return (
      <ContentFrame>
        <Box role="status">
          <Typography sx={{ mb: 1 }}>Loading the question bank…</Typography>
          <LinearProgress />
        </Box>
      </ContentFrame>
    )
  }

  if (step !== 'ready') {
    return (
      <ContentFrame>
        <PageHeading ref={headingRef}>Question bank</PageHeading>
        {step === 'notReviewer' ? (
          <Typography sx={{ mb: 2 }}>This page is for course reviewers.</Typography>
        ) : (
          <Alert severity="error" role="alert" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <RouterLink to="/mandatory-training">Back to my training</RouterLink>
      </ContentFrame>
    )
  }

  const courseTitle = enrolment?.content?.course?.title

  return (
    <ContentFrame id={PRINT_ID} maxWidth={900}>
      <GlobalStyles styles={printStyles} />
      <PageHeading ref={headingRef}>Question bank{courseTitle ? `: ${courseTitle}` : ''}</PageHeading>
      <Typography sx={{ mb: 2 }}>
        For course reviewers. This page shows every assessment question with its correct answers and feedback. Learners
        never see this information.
      </Typography>

      <Stack direction="row" gap={1.5} flexWrap="wrap" className="no-print" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Print
        </Button>
        <Button component={RouterLink} to={coursePath} variant="outlined">
          Back to the course
        </Button>
        <Button component={RouterLink} to={`${coursePath}/assessment`} variant="outlined">
          Go to the assessment
        </Button>
      </Stack>

      <SectionHeading>Assessment rules</SectionHeading>
      <Box component="ul" sx={{ mt: 0, pl: 3, '& li': { mb: 0.5 } }}>
        <Typography component="li">Pass mark: {bank.passMark}%</Typography>
        <Typography component="li">Retake policy: {bank.retakePolicy}</Typography>
        <Typography component="li">Questions in the bank: {questions.length}</Typography>
        <Typography component="li">Question order and answer order are randomised for every attempt.</Typography>
      </Box>

      <SectionHeading>Questions drawn per attempt</SectionHeading>
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          component="table"
          sx={{
            borderCollapse: 'collapse',
            width: '100%',
            '& th, & td': { border: '1px solid', borderColor: 'divider', p: 1, textAlign: 'left', fontSize: '1rem' },
          }}
        >
          <thead>
            <tr>
              <th scope="col">Pathway</th>
              <th scope="col">Questions drawn</th>
              <th scope="col">Minimum scenario questions</th>
              <th scope="col">Questions available</th>
            </tr>
          </thead>
          <tbody>
            {(bank.pathways ?? []).map((pathway) => (
              <tr key={pathway.id}>
                <th scope="row">{pathway.title}</th>
                <td>{pathway.draw}</td>
                <td>{pathway.minScenario ?? 0}</td>
                <td>
                  {questions.filter((question) => !question.pathways || question.pathways.includes(pathway.id)).length}
                </td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>

      <SectionHeading>Questions</SectionHeading>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} className="no-print" sx={{ mb: 1 }}>
        <TextField
          select
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          id="bank-filter-outcome"
          label="Filter by outcome"
          value={outcomeFilter}
          onChange={(event) => setOutcomeFilter(event.target.value)}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <option value="">All outcomes</option>
          {outcomeIds.map((outcomeId) => (
            <option key={outcomeId} value={outcomeId}>
              {outcomeId}
              {outcomes[outcomeId] ? `: ${outcomes[outcomeId].slice(0, 80)}${outcomes[outcomeId].length > 80 ? '…' : ''}` : ''}
            </option>
          ))}
        </TextField>
        <TextField
          select
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          id="bank-filter-module"
          label="Filter by module"
          value={moduleFilter}
          onChange={(event) => setModuleFilter(event.target.value)}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <option value="">All modules</option>
          {moduleIds.map((moduleId) => (
            <option key={moduleId} value={moduleId}>
              {moduleLabel(moduleId)}
            </option>
          ))}
        </TextField>
      </Stack>
      <Typography aria-live="polite" sx={{ mb: 2 }}>
        Showing {shown.length} of {questions.length} questions
        {outcomeFilter ? `, outcome ${outcomeFilter}` : ''}
        {moduleFilter ? `, module ${moduleLabel(moduleFilter)}` : ''}.
      </Typography>

      <Stack gap={2}>
        {shown.map((question) => (
          <Box
            key={question.id}
            component="section"
            className="bank-question"
            aria-labelledby={`bank-${question.id}`}
            sx={{ border: '1px solid', borderColor: 'text.secondary', borderRadius: 1, p: 2 }}
          >
            <Typography component="h3" id={`bank-${question.id}`} sx={{ fontWeight: 700 }}>
              {question.id}
            </Typography>
            <Box component="dl" sx={{ m: 0, my: 1, '& dt': { fontWeight: 700 }, '& dd': { m: 0, mb: 0.75 } }}>
              <Typography component="dt">Outcomes</Typography>
              <Typography component="dd">
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {(question.outcomes ?? []).map((outcomeId) => (
                    <li key={outcomeId}>
                      {outcomeId}
                      {outcomes[outcomeId] ? `: ${outcomes[outcomeId]}` : ''}
                    </li>
                  ))}
                </Box>
              </Typography>
              <Typography component="dt">Module</Typography>
              <Typography component="dd">{moduleLabel(question.module)}</Typography>
              <Typography component="dt">Type</Typography>
              <Typography component="dd">
                {question.type === 'multi'
                  ? `Multiple answers (select ${question.options.filter((option) => option.correct).length})`
                  : 'Single best answer'}
              </Typography>
              <Typography component="dt">Scenario question</Typography>
              <Typography component="dd">{question.scenario ? 'Yes' : 'No'}</Typography>
              <Typography component="dt">Pathways</Typography>
              <Typography component="dd">
                {question.pathways?.length ? question.pathways.map(pathwayTitle).join(', ') : 'All pathways'}
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 700, whiteSpace: 'pre-line', mb: 1 }}>{question.stem}</Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {question.options.map((option) => (
                <Box
                  component="li"
                  key={option.id}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    py: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    alignItems: 'flex-start',
                  }}
                >
                  {option.correct ? (
                    <CheckCircleIcon aria-hidden="true" color="success" sx={{ mt: '2px' }} />
                  ) : (
                    <CancelIcon aria-hidden="true" sx={{ mt: '2px', color: 'text.secondary' }} />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography>
                      <strong>{option.correct ? 'Correct' : 'Incorrect'}:</strong> {option.text}
                    </Typography>
                    {option.feedback && <Typography sx={{ color: 'text.secondary' }}>Feedback: {option.feedback}</Typography>}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
    </ContentFrame>
  )
}

export default QuestionBank
